import type { CashMovementType, CashSessionStatus, PaymentBreakdownRow } from '@/lib/types';

export type CashPeriodMode = 'today' | 'yesterday' | 'custom';

export const CASH_MOVEMENT_LABELS: Record<CashMovementType, string> = {
  entrada: 'Entrada de efectivo',
  retiro: 'Retiro de efectivo',
  devolucion: 'Devolución',
};

export const CASH_SESSION_STATUS_LABELS: Record<CashSessionStatus, string> = {
  open: 'Abierto',
  closed: 'Cerrado',
};

/** Convierte una fecha local a formato YYYY-MM-DD. */
export function toDateStr(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function todayStr(): string {
  return toDateStr(new Date());
}

export function yesterdayStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toDateStr(d);
}

/**
 * Rango [inicio, fin) del día local en ISO, para filtrar `created_at`
 * (timestamptz) según la fecha/hora de la sucursal.
 */
export function dayRange(periodDate: string): { startISO: string; endISO: string } {
  const start = new Date(`${periodDate}T00:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}

/** Etiqueta legible de una fecha YYYY-MM-DD en formato dd/mm/aaaa. */
export function formatPeriodLabel(periodDate: string): string {
  if (!periodDate) return '—';
  const [y, m, d] = periodDate.split('-');
  if (!y || !m || !d) return periodDate;
  return `${d}/${m}/${y}`;
}

export function round2(n: number): number {
  return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

export interface CashSummary {
  salesCount: number;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  returnsTotal: number;
  net: number;
  cashSales: number;
  cashIn: number;
  cashOut: number;
  cashExpected: number;
  breakdown: PaymentBreakdownRow[];
  /** Rentabilidad real de las ventas del período (costo congelado en la venta). */
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
}

/** Resultado legible de la diferencia de cierre. */
export function differenceLabel(difference: number): {
  kind: 'ok' | 'faltante' | 'sobrante';
  title: string;
} {
  const value = round2(difference);
  if (Math.abs(value) < 0.005) return { kind: 'ok', title: 'CUADRE CORRECTO' };
  if (value < 0) return { kind: 'faltante', title: 'FALTANTE' };
  return { kind: 'sobrante', title: 'SOBRANTE' };
}